import React from 'react';
import PropTypes from 'prop-types';

const FALLBACK_IMAGE = 'https://placehold.co/600x400/1a1f2e/64748b?text=No+Image';

/** Format date to a readable string. */
const formatDate = (dateStr) => {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Unknown date';
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return 'Unknown date';
  }
};

/**
 * Single news article card with image, metadata, and bookmark action.
 *
 * @param {Object} props
 */
const NewsItem = ({
  title,
  description,
  imageUrl,
  newsUrl,
  author,
  date,
  source,
  article,
  isBookmarked,
  onToggleBookmark,
}) => {
  const displayTitle = title ? title.slice(0, 60) : 'Untitled';
  const displayDescription = description ? description.slice(0, 80) : '';
  const titleTruncated = title && title.length > 60;
  const descTruncated = description && description.length > 80;

  return (
    <div className="card qs-card h-100">
      <div className="position-relative">
        {/* Source badge */}
        <span className="qs-source-badge">{source}</span>

        {/* Article image */}
        <img
          src={imageUrl || FALLBACK_IMAGE}
          className="card-img-top qs-card-img"
          alt={displayTitle || 'Article image'}
          loading="lazy"
          onError={(e) => {
            if (e.target.src !== FALLBACK_IMAGE) {
              e.target.src = FALLBACK_IMAGE;
            }
          }}
        />
      </div>

      <div className="card-body d-flex flex-column">
        <h5 className="card-title qs-card-title">
          {displayTitle}{titleTruncated ? '...' : ''}
        </h5>
        <p className="card-text qs-card-desc flex-grow-1">
          {displayDescription}{descTruncated ? '...' : ''}
        </p>
        <p className="card-text">
          <small className="qs-card-meta">
            By {author || 'Unknown'} &middot; {formatDate(date)}
          </small>
        </p>
        <div className="d-flex justify-content-between align-items-center mt-auto">
          <a
            className="btn btn-sm qs-btn-primary"
            href={newsUrl}
            target="_blank"
            rel="noreferrer noopener"
          >
            Read More
            <svg className="ms-1" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="7" y1="17" x2="17" y2="7" />
              <polyline points="7 7 17 7 17 17" />
            </svg>
          </a>
          {onToggleBookmark && (
            <button
              className={`btn btn-sm qs-bookmark-btn ${isBookmarked ? 'qs-bookmarked' : ''}`}
              onClick={() => onToggleBookmark(article)}
              title={isBookmarked ? 'Remove from bookmarks' : 'Save to bookmarks'}
              aria-label={isBookmarked ? 'Remove from bookmarks' : 'Save to bookmarks'}
            >
              {isBookmarked ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

NewsItem.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  imageUrl: PropTypes.string,
  newsUrl: PropTypes.string,
  author: PropTypes.string,
  date: PropTypes.string,
  source: PropTypes.string,
  article: PropTypes.object,
  isBookmarked: PropTypes.bool,
  onToggleBookmark: PropTypes.func,
};

NewsItem.displayName = 'NewsItem';

export default NewsItem;
